import React, { useState, useEffect } from 'react';
import { collection, getDocs, setDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import Layout from '../components/Layout';
import { Trash2, UserPlus, Shield } from 'lucide-react';

export default function AdminUsersPage() {
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('editor'); // Default role
    const [loading, setLoading] = useState(true);

    // Load allowed users
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'allowed_users'));
                const users = [];
                querySnapshot.forEach((doc) => {
                    users.push({ id: doc.id, ...doc.data() });
                });
                setAllowedUsers(users);
            } catch (error) {
                console.error("Error fetching users:", error);
            }
            setLoading(false);
        };
        fetchUsers();
    }, []);

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!email) return;

        try {
            // Add to 'allowed_users' collection
            await setDoc(doc(db, 'allowed_users', email), {
                email,
                role,
                createdAt: new Date().toISOString()
            });

            setAllowedUsers([...allowedUsers, { id: email, email, role, createdAt: new Date().toISOString() }]);
            setEmail('');
        } catch (error) {
            console.error("Error adding user:", error);
            alert("Failed to add user");
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm(`Remove access for ${userId}?`)) return;

        try {
            await deleteDoc(doc(db, 'allowed_users', userId));
            setAllowedUsers(allowedUsers.filter(user => user.id !== userId));
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    if (loading) return <div className="text-white text-center py-20">Loading...</div>;

    return (
        <Layout>
            <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
                    <Shield className="text-cyan-400" /> User Management
                </h1>

                {/* Add User Form */}
                <div className="bg-[#1a1a1a] p-6 rounded-xl border border-white/10 mb-8">
                    <h3 className="text-xl font-bold text-white mb-4">Invite New User</h3>
                    <form onSubmit={handleAddUser} className="flex gap-4 items-end">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500"
                                placeholder="newuser@example.com"
                            />
                        </div>
                        <div className="w-48">
                            <label className="block text-sm font-medium text-gray-400 mb-2">Role</label>
                            <select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500"
                            >
                                <option value="editor">Editor</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                        <button
                            type="submit"
                            className="bg-cyan-600 hover:bg-cyan-500 text-white px-6 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-colors"
                        >
                            <UserPlus size={18} /> Invite
                        </button>
                    </form>
                </div>

                {/* User List */}
                <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-white/5 text-gray-400 border-b border-white/5">
                            <tr>
                                <th className="p-4 font-medium">Email</th>
                                <th className="p-4 font-medium">Role</th>
                                <th className="p-4 font-medium">Invited At</th>
                                <th className="p-4 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {allowedUsers.map(user => (
                                <tr key={user.id} className="hover:bg-white/5 transition-colors text-gray-300">
                                    <td className="p-4">{user.email}</td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</td>
                                    <td className="p-4 text-right">
                                        <button
                                            onClick={() => handleDeleteUser(user.id)}
                                            className="text-gray-500 hover:text-red-400 transition-colors p-2"
                                            title="Revoke Access"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {allowedUsers.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="p-8 text-center text-gray-500">No users invited yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
}
